import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Settings, Trash2, Plus, List, Loader2 } from 'lucide-react';
import ConfigureService from './config/ConfigureService';
import DashboardLayout from '../components/DashboardLayout';
import { useSlotConfiguration, useBookings } from '../hooks/booking/useBookingContextSlices';
import { useAuth } from '../context/AuthContext';
import { facilitiesAPI } from '../services/api';
import { MODULES } from '../constants/modules';

const Configuration = () => {
  const { slotConfiguration, clearServiceConfig, removeServiceConfiguration } = useSlotConfiguration();
  const { reloadBookings } = useBookings();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentServiceData, setCurrentServiceData] = useState({ service: null });
  const [deleting, setDeleting] = useState(null);
  const canManageFacilityServices = hasPermission(MODULES.CONFIGURATION, 'write');

  const services = slotConfiguration?.services || [];
  const serviceDataFromState = location.state?.serviceData || null;

  useEffect(() => {
    if (serviceDataFromState) {
      setCurrentServiceData({ service: serviceDataFromState });
    }
  }, [serviceDataFromState]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-ga-text-primary mb-2">Configure Service</h1>
            <p className="text-ga-text-muted">
              Select a service and configure it based on the service category
            </p>
          </div>
        </div>

        {/* Add or remove facility services */}
        {canManageFacilityServices && (
          <div className="ga-card rounded-lg shadow-sm p-6 border-2 border-ga-primary/20 bg-ga-primary/5">
            <h2 className="text-lg font-semibold text-ga-text-primary mb-2 flex items-center gap-2">
              <List className="w-5 h-5 text-ga-primary" />
              Facility Services
            </h2>
            <p className="text-ga-text-muted text-sm mb-4">
              Add, configure, or remove services offered at your facility (e.g. Cricket Match, Practice Nets, Open Nets).
              Manage which services exist per ground before configuring slots and pricing here.
            </p>
            <Link
              to="/dashboard/facility-services"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-ga-primary text-white font-medium hover:bg-ga-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add or remove services
            </Link>
          </div>
        )}

        {/* Configure Service Section */}
        <div className="ga-card rounded-lg shadow-sm p-6">
          {services.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">Configured Services ({services.length})</h3>
              <ul className="text-sm text-blue-800 space-y-2">
                {services.map((config, idx) => (
                  <li key={idx} className="flex items-center justify-between ga-card/60 rounded-md px-3 py-2 border border-blue-100">
                    <span>
                      {config.service?.name || `Service ${idx + 1}`} 
                      ({config.service?.category === 'team_sports' ? 'Team Sports' : 'Individual Sports'})
                    </span>
                    <button
                      type="button"
                      disabled={deleting === idx}
                      onClick={async () => {
                        const serviceName = config.service?.name || `Service ${idx + 1}`;
                        if (!window.confirm(`Remove "${serviceName}" from the facility?`)) return;
                        setDeleting(idx);
                        try {
                          const sid = config.service?.id;
                          if (sid) await facilitiesAPI.deleteService(sid);
                          removeServiceConfiguration(idx);
                          if (currentServiceData?.service?.name === config.service?.name) {
                            setCurrentServiceData({ service: null });
                          }
                          await reloadBookings();
                        } catch (err) {
                          alert('Failed to remove service: ' + (err.message || 'Unknown error'));
                        } finally {
                          setDeleting(null);
                        }
                      }}
                      className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-xs font-medium flex items-center space-x-1 disabled:opacity-50"
                      title="Remove this service from facility"
                    >
                      {deleting === idx ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      <span>{deleting === idx ? '...' : 'Remove'}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {/* Danger action — placed at bottom, away from primary actions */}
              <button
                disabled={deleting === 'all'}
                onClick={async () => {
                  if (!window.confirm(`Are you sure you want to remove all ${services.length} service(s)?\n\nThis will permanently delete them from the facility.`)) return;
                  setDeleting('all');
                  try {
                    for (const config of services) {
                      const sid = config.service?.id;
                      if (sid) {
                        try { await facilitiesAPI.deleteService(sid); } catch (_) { /* best-effort */ }
                      }
                    }
                    clearServiceConfig();
                    setCurrentServiceData({ service: null });
                    await reloadBookings();
                  } catch (err) {
                    alert('Failed to remove some services: ' + (err.message || 'Unknown error'));
                  } finally {
                    setDeleting(null);
                  }
                }}
                className="mt-3 px-3 py-1.5 border border-red-300 text-red-600 rounded hover:bg-red-50 transition-colors text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
              >
                {deleting === 'all' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                <span>{deleting === 'all' ? 'Removing…' : 'Remove all services'}</span>
              </button>
            </div>
          )}

          <ConfigureService
            onComplete={(data) => {
              setCurrentServiceData({ service: data });
              // §8 rule-based slots: team-sports services go straight to the
              // single-step timings & slots rule editor (no date-range wizard).
              if (data && data.category === 'team_sports') {
                navigate('/dashboard/slot-rule', { state: { serviceData: data } });
              } else if (data && data.category === 'individual_sports') {
                navigate('/dashboard/config-individual-service', { state: { serviceData: data } });
              }
            }}
            initialData={currentServiceData.service}
            dateRange={null}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Configuration;

